import { useEffect, useMemo, useState } from 'react'
import { locationsRepository } from '@/shared/repositories/locations/locationsRepository'
import { eventsRepository } from '@/shared/repositories/events/eventsRepository'
import { shiftTimeEntriesRepository } from '@/shared/repositories/shiftTime/shiftTimeEntriesRepository'
import { shiftLocationPeriodPaymentsRepository } from '@/shared/repositories/shiftPayments/shiftLocationPeriodPaymentsRepository'
import { buildMonthPeriodKey } from '@/shared/lib/shiftPeriodPaymentKey'
import { type ShiftAnalyticsResult } from '@/shared/lib/shiftAnalytics'
import {
  buildFetchRangeForMonthSpan,
  buildPlantoesMultiMonthSummary,
  computePlantoesMonthMoney,
  MONTH_SUMMARY_FETCH_SPAN,
  monthLabel,
  monthRange,
  type MonthHoursBar,
  type PlantoesMonthMoney,
  type PreviousMonthSnapshot,
  type TwelveMonthAverage,
} from './plantoesMonthAnalytics'

export type { MonthHoursBar, PlantoesMonthMoney, PreviousMonthSnapshot, TwelveMonthAverage }

const EMPTY_MONEY: PlantoesMonthMoney = {
  receivableCents: 0,
  receivableLocationCount: 0,
  receivableLocationNames: [],
  receivableShiftCount: 0,
  receivedCents: 0,
  paidShiftCount: 0,
  receivableBreakdown: [],
  paidBreakdown: [],
  attendanceTotal: null,
}

const emptyAnalytics = (range: { start: Date; end: Date }): ShiftAnalyticsResult =>
  buildPlantoesMultiMonthSummary(
    range.start.getUTCFullYear(),
    range.start.getUTCMonth(),
    {
      entries: [],
      events: [],
      locations: [],
      includedLocationIds: [],
    },
  ).current

export const usePlantoesMonthSummary = (
  selectedMonth?: {
    year: number
    month: number
  },
  options?: { includedLocationIds?: string[] },
): {
  analytics: ShiftAnalyticsResult
  periodLabel: string
  currentShortLabel: string
  previousMonths: PreviousMonthSnapshot[]
  twelveMonthAverage: TwelveMonthAverage | null
  lastSixMonths: MonthHoursBar[]
  money: PlantoesMonthMoney
  loading: boolean
} => {
  const now = useMemo(() => new Date(), [])
  const year = selectedMonth?.year ?? now.getUTCFullYear()
  const month = selectedMonth?.month ?? now.getUTCMonth()
  const { start, end } = useMemo(() => monthRange(year, month), [year, month])

  const [analytics, setAnalytics] = useState<ShiftAnalyticsResult>(() => emptyAnalytics({ start, end }))
  const [periodLabel, setPeriodLabel] = useState('')
  const [currentShortLabel, setCurrentShortLabel] = useState('')
  const [previousMonths, setPreviousMonths] = useState<PreviousMonthSnapshot[]>([])
  const [twelveMonthAverage, setTwelveMonthAverage] = useState<TwelveMonthAverage | null>(null)
  const [money, setMoney] = useState<PlantoesMonthMoney>(EMPTY_MONEY)
  const [lastSixMonths, setLastSixMonths] = useState<MonthHoursBar[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const [{ locations }, { events }] = await Promise.all([
          locationsRepository.list(),
          eventsRepository.list(),
        ])
        if (cancelled) return

        const includedLocationIds =
          options?.includedLocationIds && options.includedLocationIds.length > 0
            ? options.includedLocationIds
            : locations.map((l) => l.id)

        const fetchRange = buildFetchRangeForMonthSpan(
          year,
          month,
          MONTH_SUMMARY_FETCH_SPAN,
          locations,
        )
        const [{ entries }, { payments }] = await Promise.all([
          shiftTimeEntriesRepository.listInRange({
            start: fetchRange.start.toISOString(),
            end: fetchRange.end.toISOString(),
          }),
          // Sem status de pagamento (offline/falha) tudo fica «a receber» em vez de travar o resumo.
          shiftLocationPeriodPaymentsRepository
            .listForOwnerInKeys([buildMonthPeriodKey(year, month)])
            .catch(() => ({ payments: [] })),
        ])
        if (cancelled) return

        const mapEvents = events.map((e) => ({
          id: e.id,
          locationId: e.locationId ?? null,
          locationText: e.location ?? null,
          startDateTime: e.startDateTime,
          endDateTime: e.endDateTime,
        }))

        const summary = buildPlantoesMultiMonthSummary(year, month, {
          entries,
          events: mapEvents,
          locations,
          includedLocationIds,
        })

        setAnalytics(summary.current)
        setPeriodLabel(summary.periodLabel)
        setCurrentShortLabel(summary.currentShortLabel)
        setPreviousMonths(summary.previousMonths)
        setTwelveMonthAverage(summary.twelveMonthAverage)
        setLastSixMonths(summary.lastSixMonths)
        setMoney(computePlantoesMonthMoney(summary.current, { year, month, locations, payments }))
      } catch {
        // Offline/falha de rede: mantém o último resumo em vez de gerar unhandled rejection
        // (SYNAP-DOC-2S). O próximo refresh do dashboard tenta de novo.
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [start, end, year, month, options?.includedLocationIds?.join(',')])

  return {
    analytics,
    periodLabel,
    currentShortLabel,
    previousMonths,
    twelveMonthAverage,
    lastSixMonths,
    money,
    loading,
  }
}

export { monthRange, monthLabel } from './plantoesMonthAnalytics'

export const currentMonthRange = () => {
  const now = new Date()
  const year = now.getUTCFullYear()
  const month = now.getUTCMonth()
  const { start, end } = monthRange(year, month)
  return {
    start,
    end,
    label: monthLabel(year, month),
    year,
    month,
  }
}
